/**
 * BriefForm — 2-column compact layout.
 *
 * Layout:
 *   Row 1: Product            | Angle
 *   Row 2: Avatar             | Problem mechanism
 *   Row 3: Solution mechanism | Awareness level
 *   Row 4: Landing page URL   |
 *   --- full width below ---
 *   Key ingredients (multi)
 *   Optional guidance (textarea)
 *   Winning structure (fixed-height textarea, scrolls internally)
 *   Variable Ideas panel (collapsed by default, below brief fields — T8)
 *
 * The 7 single-pick selects + comboboxes occupy a compact two-column grid so
 * the left column doesn't tower over the rest of the page. Multi-select and
 * textareas keep full width because they need room to render their values
 * (pills, multi-line text).
 *
 * On narrow screens (<640px) the grid collapses to one column.
 */
import type { EntryOrFreeText, IdeateRequest, ParsedIdea } from '@malaberg/shared';
import { Input, SegmentedControl, SimpleGrid, Stack, Textarea } from '@mantine/core';
import { type BriefFormMode, useBriefFormStore } from '../../state/brief-form-store';
import { AwarenessLevelSelect } from './AwarenessLevelSelect';
import { GenerateButton } from './GenerateButton';
import { GenerationModelSelect } from './GenerationModelSelect';
import { GenerationQueuePanel } from './GenerationQueuePanel';
import { IngredientsMultiSelect } from './IngredientsMultiSelect';
import { LibraryCombobox } from './LibraryCombobox';
import { ProductSelect } from './ProductSelect';
import { VariableIdeasPanel } from './VariableIdeasPanel';

export function BriefForm() {
  const angleEntryId = useBriefFormStore((s) => s.angleEntryId);
  const avatarEntryId = useBriefFormStore((s) => s.avatarEntryId);
  const problemMechEntryId = useBriefFormStore((s) => s.problemMechEntryId);
  const solutionMechEntryId = useBriefFormStore((s) => s.solutionMechEntryId);
  const ingredientEntryIds = useBriefFormStore((s) => s.ingredientEntryIds);
  const awarenessLevelEntryId = useBriefFormStore((s) => s.awarenessLevelEntryId);
  const landingPageUrlEntryId = useBriefFormStore((s) => s.landingPageUrlEntryId);
  const optionalGuidance = useBriefFormStore((s) => s.optionalGuidance);
  const winningStructure = useBriefFormStore((s) => s.winningStructure);
  const sourceCopy = useBriefFormStore((s) => s.sourceCopy);
  const mode = useBriefFormStore((s) => s.mode);
  const generationModelKey = useBriefFormStore((s) => s.generationModelKey);
  const setField = useBriefFormStore((s) => s.setField);

  // ── T8: VariableIdeasPanel wiring ─────────────────────────────────────────
  const productId = useBriefFormStore((s) => s.productId);
  const market = useBriefFormStore((s) => s.market);
  const angleField = useBriefFormStore((s) => s.angleField);
  const avatarField = useBriefFormStore((s) => s.avatarField);
  const problemMechField = useBriefFormStore((s) => s.problemMechField);
  const solutionMechField = useBriefFormStore((s) => s.solutionMechField);
  const awarenessLevelField = useBriefFormStore((s) => s.awarenessLevelField);
  const landingPageUrlField = useBriefFormStore((s) => s.landingPageUrlField);
  const ingredientFields = useBriefFormStore((s) => s.ingredientFields);
  const applyIdeaSet = useBriefFormStore((s) => s.applyIdeaSet);

  // Build a partial-brief snapshot from whatever fields are currently filled in.
  // Only include non-null fields so the ideation prompt isn't cluttered with
  // empty placeholders. Uses the same EntryOrFreeText wire shape the brief uses
  // on submission — ephemeral values pass through unchanged.
  function toWire(field: typeof angleField): EntryOrFreeText | undefined {
    if (!field) return undefined;
    if (field.kind === 'entry') return { entryId: field.entryId };
    if (field.kind === 'value') return { value: field.value };
    return { ephemeralValue: field.ephemeralValue };
  }

  const partialBrief: IdeateRequest['partialBrief'] = {
    ...(angleField ? { angle: toWire(angleField) } : {}),
    ...(avatarField ? { avatar: toWire(avatarField) } : {}),
    ...(problemMechField ? { problemMechanism: toWire(problemMechField) } : {}),
    ...(solutionMechField ? { solutionMechanism: toWire(solutionMechField) } : {}),
    ...(awarenessLevelField ? { awarenessLevel: toWire(awarenessLevelField) } : {}),
    ...(ingredientFields.length > 0
      ? {
          ingredients: ingredientFields
            .map((f) => toWire(f))
            .filter((v): v is EntryOrFreeText => v !== undefined),
        }
      : {}),
  };

  const handleApplyIdea = (idea: ParsedIdea) => {
    applyIdeaSet(idea);
  };

  return (
    <Stack gap="sm">
      <Input.Wrapper label="Generation type">
        <SegmentedControl
          fullWidth
          size="xs"
          aria-label="Generation type"
          value={mode}
          onChange={(value) => setField('mode', value as BriefFormMode)}
          data={[
            { label: 'Use existing structure', value: 'use_existing_structure' },
            { label: 'Write new structure', value: 'scratch_premise_set' },
            { label: 'Facebook ad to advertorial', value: 'fb_ad_to_advertorial' },
            {
              label: 'Competitor advertorial to advertorial',
              value: 'competitor_advertorial_to_advertorial',
            },
          ]}
          styles={{
            label: {
              minHeight: 34,
              whiteSpace: 'normal',
              lineHeight: 1.15,
              paddingBlock: 6,
            },
          }}
          data-testid="brief-mode-control"
        />
      </Input.Wrapper>

      <GenerationModelSelect />

      {/* Single-pick fields in a compact 2-column grid. */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <ProductSelect />

        <LibraryCombobox
          type="angle"
          label="Angle"
          placeholder="Search or add a new angle"
          value={angleEntryId}
          onChange={(id) => setField('angleEntryId', id)}
          fieldValue={angleField}
          onFieldChange={(fv) => setField('angleField', fv)}
          canAddNew
          data-testid="field-angle"
        />

        <LibraryCombobox
          type="avatar"
          label="Avatar"
          placeholder="Search or add a new avatar"
          value={avatarEntryId}
          onChange={(id) => setField('avatarEntryId', id)}
          fieldValue={avatarField}
          onFieldChange={(fv) => setField('avatarField', fv)}
          canAddNew
          data-testid="field-avatar"
        />

        <LibraryCombobox
          type="problem_mechanism"
          label="Problem mechanism"
          placeholder="Search or add a new problem mechanism"
          value={problemMechEntryId}
          onChange={(id) => setField('problemMechEntryId', id)}
          fieldValue={problemMechField}
          onFieldChange={(fv) => setField('problemMechField', fv)}
          canAddNew
          data-testid="field-problem-mechanism"
        />

        <LibraryCombobox
          type="solution_mechanism"
          label="Solution mechanism"
          placeholder="Search or add a new solution mechanism"
          value={solutionMechEntryId}
          onChange={(id) => setField('solutionMechEntryId', id)}
          fieldValue={solutionMechField}
          onFieldChange={(fv) => setField('solutionMechField', fv)}
          canAddNew
          data-testid="field-solution-mechanism"
        />

        <AwarenessLevelSelect
          value={awarenessLevelEntryId}
          onChange={(id) => setField('awarenessLevelEntryId', id)}
          fieldValue={awarenessLevelField}
          onFieldChange={(fv) => setField('awarenessLevelField', fv)}
        />

        <LibraryCombobox
          type="landing_page_url"
          label="Landing page URL"
          placeholder="Search or add a landing page URL"
          value={landingPageUrlEntryId}
          onChange={(id) => setField('landingPageUrlEntryId', id)}
          fieldValue={landingPageUrlField}
          onFieldChange={(fv) => setField('landingPageUrlField', fv)}
          canAddNew
          data-testid="field-landing-page-url"
        />
      </SimpleGrid>

      {/* Multi-select needs full width to hold all selected pills. */}
      <IngredientsMultiSelect
        value={ingredientEntryIds}
        onChange={(ids) => setField('ingredientEntryIds', ids)}
        fieldValues={ingredientFields}
        onFieldValuesChange={(fvs) => setField('ingredientFields', fvs)}
      />

      {/* Free-text fields — full width for readability. */}
      <Textarea
        label="Optional guidance"
        placeholder="Anything specific you want this ad to do?"
        minRows={3}
        autosize
        value={optionalGuidance}
        onChange={(e) => setField('optionalGuidance', e.currentTarget.value)}
        data-testid="field-optional-guidance"
      />

      {mode === 'use_existing_structure' && (
        <Textarea
          label="Winning structure"
          placeholder="Paste the source ad structure here"
          rows={8}
          value={winningStructure}
          onChange={(e) => setField('winningStructure', e.currentTarget.value)}
          data-testid="field-winning-structure"
        />
      )}

      {mode === 'fb_ad_to_advertorial' && (
        <Textarea
          label="Source Facebook ad"
          placeholder="Paste the source Facebook ad"
          rows={8}
          value={sourceCopy}
          onChange={(e) => setField('sourceCopy', e.currentTarget.value)}
          data-testid="field-source-copy"
        />
      )}

      {mode === 'competitor_advertorial_to_advertorial' && (
        <Textarea
          label="Source competitor advertorial copy"
          placeholder="Paste the source competitor advertorial copy"
          rows={8}
          value={sourceCopy}
          onChange={(e) => setField('sourceCopy', e.currentTarget.value)}
          data-testid="field-source-copy"
        />
      )}

      <GenerateButton />

      <GenerationQueuePanel />

      {/* T8: Variable Ideas panel — collapsed by default, below brief fields. */}
      <VariableIdeasPanel
        productId={productId}
        market={market}
        generationModelKey={generationModelKey}
        partialBrief={partialBrief}
        onApplyIdea={handleApplyIdea}
      />
    </Stack>
  );
}
